import type { AdminPermission } from "../api/generated/models/AdminPermission";
import type {
  AdminNavGroupItem,
  AdminNavItem,
  AdminNavSection,
  AdminRouteRule,
} from "../config/adminNav";

export const OPEN_GROUPS_STORAGE_KEY = "adminNav.openGroups.v1";

export type OpenGroupOverrides = Record<string, boolean>;

const toSegments = (path: string): string[] => path.split("/").filter(Boolean);

// Segment-wise prefix match with `:param` wildcards: the pattern matches itself
// and any descendant of it, never a sibling that merely shares a string prefix.
export const matchesPath = (pathname: string, pattern: string): boolean => {
  const path = toSegments(pathname);
  const pat = toSegments(pattern);
  if (pat.length === 0) return path.length === 0;
  if (pat.length > path.length) return false;
  return pat.every((segment, i) =>
    segment.startsWith(":") ? path[i].length > 0 : segment === path[i]
  );
};

export const isNavPathActive = (
  pathname: string,
  item: { path: string; matchPaths?: string[] }
): boolean =>
  [item.path, ...(item.matchPaths ?? [])].some((pattern) =>
    matchesPath(pathname, pattern)
  );

export const isGroupActive = (
  pathname: string,
  group: AdminNavGroupItem
): boolean => group.items.some((leaf) => isNavPathActive(pathname, leaf));

// Filtering children first and dropping empty groups is the OR-of-children rule,
// and guarantees a rendered group never contains an item the admin cannot see.
export const buildVisibleNav = (
  sections: AdminNavSection[],
  hasPermission: (permission: AdminPermission) => boolean
): AdminNavSection[] =>
  sections
    .map((section) => ({
      ...section,
      items: section.items.reduce<AdminNavItem[]>((acc, item) => {
        if (item.kind === "link") {
          if (!item.permission || hasPermission(item.permission)) acc.push(item);
          return acc;
        }
        const items = item.items.filter(
          (leaf) => !leaf.permission || hasPermission(leaf.permission)
        );
        if (items.length > 0) acc.push({ ...item, items });
        return acc;
      }, []),
    }))
    .filter((section) => section.items.length > 0);

// One rule per pattern a row lights up on, carrying that row's permission, so the
// route guard and the sidebar can never disagree about who may open a page.
export const collectAdminRouteRules = (
  sections: AdminNavSection[],
  unlisted: AdminRouteRule[] = []
): AdminRouteRule[] => [
  ...sections.flatMap((section) =>
    section.items.flatMap((item) =>
      (item.kind === "group" ? item.items : [item]).flatMap((row) =>
        [row.path, ...(row.matchPaths ?? [])].map((pattern) => ({
          pattern,
          permission: row.permission,
        }))
      )
    )
  ),
  ...unlisted,
];

// matchesPath also accepts descendants, so the deepest matching pattern wins:
// the most specific row owns the route.
export const findAdminRouteRule = (
  pathname: string,
  rules: AdminRouteRule[]
): AdminRouteRule | undefined =>
  rules.reduce<AdminRouteRule | undefined>((best, rule) => {
    if (!matchesPath(pathname, rule.pattern)) return best;
    if (best && toSegments(best.pattern).length >= toSegments(rule.pattern).length) {
      return best;
    }
    return rule;
  }, undefined);

// Fails closed: a path with no rule is refused, so a new admin route stays shut
// until it is given a sidebar row or an entry in UNLISTED_ADMIN_ROUTES.
export const canOpenAdminRoute = (
  pathname: string,
  rules: AdminRouteRule[],
  isAdmin: boolean,
  hasPermission: (permission: AdminPermission) => boolean
): boolean => {
  if (!isAdmin) return false;
  const rule = findAdminRouteRule(pathname, rules);
  if (!rule) return false;
  return !rule.permission || hasPermission(rule.permission);
};

export const collectGroupIds = (sections: AdminNavSection[]): string[] =>
  sections.flatMap((section) =>
    section.items.filter((item) => item.kind === "group").map((item) => item.id)
  );

const defaultOpen = (pathname: string, group: AdminNavGroupItem): boolean =>
  group.items.length === 1 || isGroupActive(pathname, group);

// An absent override means "no opinion", so the active group opens itself while a
// group the admin deliberately collapsed stays collapsed as they navigate into it.
export const isGroupOpen = (
  overrides: OpenGroupOverrides,
  pathname: string,
  group: AdminNavGroupItem
): boolean => overrides[group.id] ?? defaultOpen(pathname, group);

export const parseOpenGroups = (
  raw: string | null,
  knownIds: string[]
): OpenGroupOverrides => {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  const known = new Set(knownIds);
  return Object.entries(parsed as Record<string, unknown>).reduce<OpenGroupOverrides>(
    (acc, [id, value]) => {
      if (known.has(id) && typeof value === "boolean") acc[id] = value;
      return acc;
    },
    {}
  );
};

export const readOpenGroups = (knownIds: string[]): OpenGroupOverrides => {
  try {
    return parseOpenGroups(localStorage.getItem(OPEN_GROUPS_STORAGE_KEY), knownIds);
  } catch {
    return {};
  }
};

export const writeOpenGroups = (overrides: OpenGroupOverrides): void => {
  try {
    localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Storage disabled (private mode, quota) — expansion state is chrome, not data.
  }
};
